import { Box, Chip, Stack, Tab, Tabs, Typography, Button, CircularProgress } from '@mui/material';
import palette from '../../shared/theme/palette';
import Panel from '../../components/Panel';
import ViewAllDialog from '../../components/ViewAllDialog';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../shared/service/dataprovider';
import moment from 'moment';
import { formatter } from '../../shared/util';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lowerCase, startCase, upperFirst } from 'lodash';
import { adjustedCurrentBalance } from './unpostedPayFirstLedger';

export default function ResmanDetail({ lease, person }) {
  const { t } = useTranslation();
  const [showViewAll, setShowViewAll] = useState(false);
  const [selectedTab, setSelectedTab] = useState('resman');

  const { isLoading, error, data } = useQuery({
    queryKey: [`transactionHistory/${lease.id}`],
    queryFn: () =>
      api.get(`/v1/admin/lease/transaction-history`, { params: { leaseId: lease.id, dateFrom: lease.LeaseStartDate, dateTo: lease.LeaseEndDate } }),
  });
  const transactions = data?.data?.transactions;
  const unpostedCredit = Number(data?.data?.unpostedPayFirstCredit) || 0;
  const balanceTransaction = currentBalanceTransaction(person, unpostedCredit);
  if (isLoading) {
    return (
      <Panel sx={{ textAlign: 'center' }} padding={6}>
        <CircularProgress color="primary" />
      </Panel>
    );
  }
  if (error) {
    return (
      <Typography variant="h6" sx={{ color: palette.error[50] }}>
        {error?.response?.data?.message ?? `There is an error for loading transaction history for lease ${lease.id}. Try again later`}
      </Typography>
    );
  }
  if (!transactions) return null;

  const firstTransactions = transactions.slice(0, 5);

  return (
    <Panel padding={0} sx={{ px: 3, pt: 2.5, pb: 0 }}>
      <Tabs
        value={selectedTab}
        onChange={(_e, value) => setSelectedTab(value)}
        sx={{ minHeight: 'auto', mb: 2, width: '100%', borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="resman" label={t('lease.transaction.title1')} sx={{ py: 1, minHeight: 'auto', flex: 1 }} />
        <Tab value="transactions" label={t('lease.transaction.title2')} sx={{ py: 1, minHeight: 'auto', flex: 1 }} />
      </Tabs>
      {selectedTab === 'resman' ? (
        <Stack gap={3} sx={{ mb: 2 }}>
          {balanceTransaction ? <ResmanLedgerItem key={person.id + '_balance'} transaction={balanceTransaction} /> : <></>}
          {firstTransactions.map(transaction => (
            <ResmanLedgerItem key={transaction.transactionId} transaction={transaction} />
          ))}
        </Stack>
      ) : (
        <Stack gap={3} sx={{ mb: 2 }}>
          {balanceTransaction ? <ResmanLedgerItem key={person.id + '_balance'} transaction={balanceTransaction} /> : <></>}
          {person.relatedUser?.transactions ? (
            <>
              {person.relatedUser.transactions
                .filter(t => t.leaseId === lease?.id)
                .sort((t1, t2) => new Date(t2.createdDate) - new Date(t1.createdDate))
                .slice(0, 5)
                .map(transaction => (
                  <TransactionItem key={transaction.id} transaction={transaction} person={person} />
                ))}
            </>
          ) : person.balance && person.balance.length ? (
            <></>
          ) : (
            <Typography>{t('lease.transaction.empty')}</Typography>
          )}
        </Stack>
      )}
      <Button fullWidth variant="text" onClick={() => setShowViewAll(true)} sx={{ fontWeight: 500 }}>
        {t('lease.transaction.viewAll')}
      </Button>
      {showViewAll && <ViewAllModal person={person} transactions={transactions} lease={lease} balanceTransaction={balanceTransaction} onClose={() => setShowViewAll(false)} />}
    </Panel>
  );
}

function ViewAllModal({ person, transactions, onClose, lease, balanceTransaction }) {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState('resman');

  return (
    <ViewAllDialog
      open
      onClose={onClose}
      title={t('lease.transaction.viewAll')}
      header={
        <Tabs
          value={selectedTab}
          onChange={(_e, value) => setSelectedTab(value)}
          sx={{ minHeight: '36px', mb: 1, width: '100%', borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="resman" label={t('lease.transaction.title1')} sx={{ py: 1, minHeight: 'auto', flex: 1 }} />
          <Tab value="transactions" label={t('lease.transaction.title2')} sx={{ py: 1, minHeight: 'auto', flex: 1 }} />
        </Tabs>
      }
    >
      {selectedTab === 'resman' ? (
        <Stack gap={3} sx={{ pt: 1 }}>
          {balanceTransaction ? <ResmanLedgerItem key={person.id + '_balance'} transaction={balanceTransaction} /> : <></>}
          {transactions.map(transaction => (
            <ResmanLedgerItem key={transaction.transactionId} transaction={transaction} />
          ))}
        </Stack>
      ) : (
        <Stack gap={3} sx={{ pt: 1 }}>
          {balanceTransaction ? <ResmanLedgerItem key={person.id + '_balance'} transaction={balanceTransaction} /> : <></>}
          {person.relatedUser?.transactions ? (
            <>
              {person.relatedUser.transactions
                .filter(t => t.leaseId === lease?.id)
                .sort((t1, t2) => new Date(t2.createdDate) - new Date(t1.createdDate))
                .map(transaction => (
                  <TransactionItem key={transaction.id} transaction={transaction} person={person} />
                ))}
            </>
          ) : (
            <Typography>{t('lease.transaction.empty')}</Typography>
          )}
        </Stack>
      )}
    </ViewAllDialog>
  );
}

function TransactionItem({ transaction, person }) {
  const paymentMethod = person?.relatedUser?.paymentMethods?.find(pM => pM.gatewayPayerId === transaction.gatewayPayerId);

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Box>
        <Typography color="grey.800" sx={{ fontWeight: 600 }}>
          {paymentMethod?.type === 'CARD'
            ? `Payment Initiated - xxxx${paymentMethod.lastFourDigits}`
            : paymentMethod?.type === 'BANK'
              ? `Payment Initiated - ACH`
              : transaction.paymentMode}
        </Typography>
        <Typography variant="caption" color="grey.500">
          {transaction.createdDate
            ? `${moment(transaction.createdDate).format('MM/DD/YY')}(${startCase(lowerCase(transaction.paymentMode))}) - Txn: ${transaction.transactionId ?? 'N.A'}`
            : ''}
        </Typography>
        <br></br>
        <Typography variant="caption" color="grey.500" fontWeight={'bold'}>
          {transaction.status === 'FAILED' ? `Failed Reason: ` + transaction.failureReason : ``}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography color="grey.800" sx={{ fontWeight: 600 }}>
          {formatter.format(transaction.totalAmount)}
        </Typography>
        <Chip variant="filled" size="small" label={transaction.status} sx={{ ...(colors[transaction.status] ?? {}) }} />
      </Box>
    </Stack>
  );
}

function currentBalanceTransaction(person, credit) {
  if (!person.balance || !person.balance.length) return null;
  return {
    TransactionCategory: 'Current Balance',
    date: new Date(),
    amount: '' + adjustedCurrentBalance(person.balance[0].Balance, credit),
    type: 'Total',
  };
}

function getDescription(transaction) {
  if (transaction.description?.includes('Credit Build Service')) {
    return 'CredBuild Charges';
  }
  return transaction.TransactionCategory ?? transaction.category?.name;
}
function ResmanLedgerItem({ transaction }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Box>
        <Typography color="grey.800" sx={{ fontWeight: 600 }}>
          {getDescription(transaction)}
        </Typography>
        <Typography variant="caption" color="grey.500">
          {transaction.date ? moment(transaction.date).format('MM/DD/YY') : ''}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography color="grey.800" sx={{ fontWeight: 600 }}>
          {formatter.format(transaction.amount ?? 0)}
        </Typography>
        <Chip variant="filled" size="small" label={transaction.type} sx={{ ...(colors[transaction.type] ?? {}) }} />
      </Box>
    </Stack>
  );
}

const colors = {
  Payment: {
    backgroundColor: palette.success[70],
    color: palette.success[50],
  },
  Rent: {
    backgroundColor: palette.primary[10],
    color: palette.primary[40],
  },
  APPROVED: {
    backgroundColor: palette.success[70],
    color: palette.success[50],
  },
  FAILED: {
    backgroundColor: palette.error[75],
    color: palette.error[50],
  },
  Total: {
    backgroundColor: palette.primary[10],
    color: palette.primary[40],
  },
};
